/**
 * SubmissionFileList — list of files a student submitted.
 * Fetches presigned download-url from R2 per file on demand.
 * Mobile-first: full-width download buttons, ellipsis on long filenames.
 */
import { useState } from 'react';
import { Download, FileX } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getSubmissionFileDownloadUrl } from '@client/lib/queries/use-teacher';
import type { SubmissionFile } from '@client/lib/queries/use-teacher';

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  submissionId: string;
  files: SubmissionFile[];
}

export function SubmissionFileList({ submissionId, files }: Props) {
  if (files.length === 0) {
    return (
      <span className="text-xs text-muted-foreground italic">Không có file đính kèm</span>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {files.map((f) => (
        <FileDownloadButton key={f.id} submissionId={submissionId} file={f} />
      ))}
    </div>
  );
}

function FileDownloadButton({
  submissionId,
  file,
}: {
  submissionId: string;
  file: SubmissionFile;
}) {
  const [loading, setLoading] = useState(false);

  async function handleDownload() {
    setLoading(true);
    try {
      const url = await getSubmissionFileDownloadUrl(submissionId, file.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Không tải được file. Thử lại sau.');
    } finally {
      setLoading(false);
    }
  }

  const sizeLabel = formatBytes(file.fileSize);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void handleDownload()}
      disabled={loading}
      className="w-full justify-start gap-2 min-h-[44px] text-left"
    >
      {loading ? (
        <FileX className="h-4 w-4 shrink-0 text-muted-foreground animate-pulse" />
      ) : (
        <Download className="h-4 w-4 shrink-0" />
      )}
      <span className="truncate text-xs flex-1">{file.fileName}</span>
      {sizeLabel && (
        <span className="text-xs text-muted-foreground shrink-0">{sizeLabel}</span>
      )}
    </Button>
  );
}
